import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  LockKeyhole,
  Plus,
  Share2,
  Sparkles,
  X,
} from "lucide-react";
import hero from "./assets/family-roundtable.png";
import booklet from "./assets/consensus-booklet.png";
import {
  buildReport,
  defaultSurvey,
  type ConsensusReport,
  type ImageryTag,
  type PriorityKey,
  type SurveyInput,
} from "./lib/algorithm";
import { shareReport } from "./lib/share";
import { activationClient } from "./services/activation";

type Screen = "home" | "wizard" | "report";
const tags: ImageryTag[] = ["自然", "学识", "温柔", "勇气", "开阔", "笃定"];
const priorityLabels: Record<PriorityKey, string> = {
  sound: "读起来顺口",
  glyph: "写起来舒服",
  imagery: "意象有共鸣",
  distinction: "不容易撞名",
};

function Seal({ children }: { children: string }) {
  return <span className="seal">{children}</span>;
}
function PaperSection({
  eyebrow,
  title,
  children,
  id,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section className="paper-section" id={id}>
      <div className="section-head">
        <span>{eyebrow}</span>
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}
function ScoreBar({
  label,
  value,
  max,
  color = "green",
}: {
  label: string;
  value: number;
  max: number;
  color?: "green" | "red";
}) {
  return (
    <div className="score-row">
      <div>
        <span>{label}</span>
        <b>{Math.round(value)}</b>
      </div>
      <div className="bar">
        <i
          className={color}
          style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
        />
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const [step, setStep] = useState(1);
  const [input, setInput] = useState<SurveyInput>(defaultSurvey);
  const [preview, setPreview] = useState<ConsensusReport | null>(null);
  const [full, setFull] = useState<ConsensusReport | null>(null);
  const [unlock, setUnlock] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const candidates = input.candidates.filter(Boolean);
  const canNext =
    step === 1
      ? input.surname.trim().length > 0 && candidates.length >= 2
      : true;
  const update = <K extends keyof SurveyInput>(key: K, value: SurveyInput[K]) =>
    setInput((v) => ({ ...v, [key]: value }));
  const runPreview = () => {
    try {
      const r = buildReport({ ...input, candidates });
      setPreview(r);
      setFull(null);
      setScreen("report");
      window.scrollTo(0, 0);
    } catch (e) {
      setNotice((e as Error).message);
    }
  };
  const unlockReport = async () => {
    setBusy(true);
    setNotice("");
    try {
      await activationClient.activate(code);
      const data = await activationClient.report({ ...input, candidates });
      setFull(data.report as ConsensusReport);
      setUnlock(false);
      setNotice(`完整报告已生成 · 剩余权益 ${data.quotaLeft}`);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (screen === "home")
    return (
      <Home
        onStart={() => {
          setScreen("wizard");
          window.scrollTo(0, 0);
        }}
      />
    );
  if (screen === "wizard")
    return (
      <Wizard
        step={step}
        input={input}
        update={update}
        setStep={setStep}
        canNext={canNext}
        onBack={() => (step === 1 ? setScreen("home") : setStep(step - 1))}
        onFinish={runPreview}
      />
    );
  return (
    <ReportPage
      report={full || preview!}
      locked={!full}
      input={input}
      notice={notice}
      onBack={() => setScreen("wizard")}
      onUnlock={() => setUnlock(true)}
      onShare={async () => setNotice(await shareReport(full!))}
      modal={
        unlock ? (
          <Unlock
            code={code}
            setCode={setCode}
            busy={busy}
            notice={notice}
            close={() => setUnlock(false)}
            submit={unlockReport}
          />
        ) : null
      }
    />
  );
}

function Home({ onStart }: { onStart: () => void }) {
  return (
    <main className="home">
      <nav>
        <div className="brand">
          <Seal>名</Seal>
          <span>宝宝名字共识报告</span>
        </div>
        <button className="text-btn" onClick={onStart}>
          开始对照
        </button>
      </nav>
      <header className="hero">
        <div className="hero-copy">
          <span className="kicker">东方新生礼册 · 家庭圆桌</span>
          <h1>
            名字不靠谁
            <br />
            声音更大。
          </h1>
          <p>
            把 2—5
            个候选名放在同一张圆桌上，看看全家真正重视什么，为什么更接近共识。
          </p>
          <button className="primary" onClick={onStart}>
            开启家庭圆桌 <ArrowRight size={18} />
          </button>
          <div className="trust">
            <span>
              <Check />
              固定可解释算法
            </span>
            <span>
              <Check />
              不算命，不定命
            </span>
            <span>
              <Check />
              手机完成约 6 分钟
            </span>
          </div>
        </div>
        <div className="hero-art">
          <img src={hero} alt="家人围桌讨论宝宝名字候选" />
          <div className="floating-note">
            <b>4 个候选</b>
            <span>先比较，再决定</span>
          </div>
        </div>
      </header>
      <section className="story">
        <div className="story-art">
          <img src={booklet} alt="候选名签汇聚成家庭共识礼册" />
        </div>
        <div>
          <span className="kicker">不是再生成一批名字</span>
          <h2>而是把“喜欢”说清楚</h2>
          <p>
            妈妈在意意象，爸爸在意读音，长辈在意传承。报告把这些标准放回各自的位置，让分歧变得可以讨论。
          </p>
          <ul>
            <li>候选总榜与家庭偏好矩阵</li>
            <li>音律、字形、意象、辨识度图形</li>
            <li>淘汰路径、风险表与下一轮对话脚本</li>
          </ul>
        </div>
      </section>
      <section className="boundary">
        <Seal>界</Seal>
        <div>
          <h3>我们不替孩子预测命运</h3>
          <p>
            报告只整理候选名的语言特征、日常使用与家庭偏好；最终请再核对户籍可用字、方言谐音和家族避讳。
          </p>
        </div>
      </section>
      <footer>宝宝名字共识报告 · 给新生命的第一场温柔讨论</footer>
    </main>
  );
}

function Wizard({
  step,
  input,
  update,
  setStep,
  canNext,
  onBack,
  onFinish,
}: {
  step: number;
  input: SurveyInput;
  update: <K extends keyof SurveyInput>(k: K, v: SurveyInput[K]) => void;
  setStep: (n: number) => void;
  canNext: boolean;
  onBack: () => void;
  onFinish: () => void;
}) {
  const setCandidate = (i: number, v: string) => {
    const arr = [...input.candidates];
    arr[i] = v.replace(/\s+/g, "").slice(0, 2);
    update("candidates", arr);
  };
  const names = input.candidates.filter(Boolean);
  return (
    <main className="wizard">
      <nav>
        <button className="icon-btn" onClick={onBack} aria-label="返回">
          <ArrowLeft />
        </button>
        <div className="progress">
          <i style={{ width: `${(step / 3) * 100}%` }} />
        </div>
        <span>{step}/3</span>
      </nav>
      <div className="wizard-card">
        <div className="step-mark">第 {step} 折</div>
        {step === 1 && (
          <>
            <h1>先把候选名签摆上桌</h1>
            <p>只填名字，不需要生辰信息。至少 2 个，最多 5 个。</p>
            <label className="field">
              <span>姓氏</span>
              <input
                value={input.surname}
                onChange={(e) =>
                  update(
                    "surname",
                    e.target.value.replace(/\s+/g, "").slice(0, 2),
                  )
                }
                placeholder="如：林"
              />
            </label>
            <div className="candidate-list">
              {input.candidates.map((n, i) => (
                <label className="name-slip" key={i}>
                  <span>候选 {i + 1}</span>
                  <div>
                    <b>{input.surname || "姓"}</b>
                    <input
                      value={n}
                      onChange={(e) => setCandidate(i, e.target.value)}
                      placeholder="名字"
                    />
                    {input.candidates.length > 2 && (
                      <button
                        onClick={() =>
                          update(
                            "candidates",
                            input.candidates.filter((_, x) => x !== i),
                          )
                        }
                        aria-label="移除"
                      >
                        <X size={17} />
                      </button>
                    )}
                  </div>
                </label>
              ))}
              {input.candidates.length < 5 && (
                <button
                  className="add-slip"
                  onClick={() =>
                    update("candidates", [...input.candidates, ""])
                  }
                >
                  <Plus />
                  再放一张名签
                </button>
              )}
            </div>
            <label className="field">
              <span>家族避讳 / 不希望出现的字（可填多个）</span>
              <input
                value={input.forbiddenChars}
                onChange={(e) => update("forbiddenChars", e.target.value)}
                placeholder="如：建、国、德"
              />
              <small className="field-hint">
                可直接连续输入，也可用顿号、逗号或空格分隔。
              </small>
            </label>
            <label className="field">
              <span>家族希望出现的字（可填多个）</span>
              <input
                value={input.preferredChars}
                onChange={(e) => update("preferredChars", e.target.value)}
                placeholder="如：安、宁"
              />
              <small className="field-hint">
                命中时会计入家庭共识分，并在报告中说明。
              </small>
            </label>
          </>
        )}
        {step === 2 && (
          <>
            <h1>请三位代表各投两枚筹码</h1>
            <p>
              一枚“首选”，一枚“暂时保留”。这不是否决，只是把分歧放到桌面上。
            </p>
            <div className="vote-grid">
              {input.votes.map((v, i) => (
                <div className="vote-card" key={v.role}>
                  <b>{v.role}</b>
                  <label>
                    <span>首选</span>
                    <select
                      value={v.favorite}
                      onChange={(e) => {
                        const votes = [...input.votes];
                        votes[i] = { ...v, favorite: e.target.value };
                        update("votes", votes);
                      }}
                    >
                      {names.map((n) => (
                        <option key={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>暂时保留</span>
                    <select
                      value={v.reservation}
                      onChange={(e) => {
                        const votes = [...input.votes];
                        votes[i] = { ...v, reservation: e.target.value };
                        update("votes", votes);
                      }}
                    >
                      {names.map((n) => (
                        <option key={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ))}
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <h1>最后说清楚：你们在意什么</h1>
            <p>这些优先级会改变报告权重，但家庭共识和日常实用始终保留。</p>
            <div className="sliders">
              {(Object.keys(priorityLabels) as PriorityKey[]).map((k) => (
                <label key={k}>
                  <div>
                    <span>{priorityLabels[k]}</span>
                    <b>{input.priorities[k]}</b>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={input.priorities[k]}
                    onChange={(e) =>
                      update("priorities", {
                        ...input.priorities,
                        [k]: Number(e.target.value),
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <div className="field">
              <span>希望名字带来的意象（可多选）</span>
              <div className="chips">
                {tags.map((t) => (
                  <button
                    className={input.imagery.includes(t) ? "active" : ""}
                    key={t}
                    onClick={() =>
                      update(
                        "imagery",
                        input.imagery.includes(t)
                          ? input.imagery.filter((x) => x !== t)
                          : [...input.imagery, t],
                      )
                    }
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="triple">
              <Choice
                label="生僻字容忍"
                value={input.rareTolerance}
                set={(v) => update("rareTolerance", v)}
              />
              <Choice
                label="重名敏感"
                value={input.duplicateSensitivity}
                set={(v) => update("duplicateSensitivity", v)}
              />
              <Choice
                label="风险核对"
                value={input.riskStrictness}
                set={(v) => update("riskStrictness", v)}
              />
            </div>
          </>
        )}
        <div className="wizard-actions">
          <button className="secondary" onClick={onBack}>
            上一步
          </button>
          <button
            className="primary"
            disabled={!canNext}
            onClick={() => (step < 3 ? setStep(step + 1) : onFinish())}
          >
            {step < 3 ? "下一折" : "生成共识预览"}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </main>
  );
}
function Choice({
  label,
  value,
  set,
}: {
  label: string;
  value: "low" | "medium" | "high";
  set: (v: "low" | "medium" | "high") => void;
}) {
  return (
    <label className="choice">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => set(e.target.value as "low" | "medium" | "high")}
      >
        <option value="low">宽松</option>
        <option value="medium">适中</option>
        <option value="high">严格</option>
      </select>
    </label>
  );
}

function ReportPage({
  report,
  locked,
  input,
  notice,
  onBack,
  onUnlock,
  onShare,
  modal,
}: {
  report: ConsensusReport;
  locked: boolean;
  input: SurveyInput;
  notice: string;
  onBack: () => void;
  onUnlock: () => void;
  onShare: () => void;
  modal: React.ReactNode;
}) {
  const top = report.candidates[0];
  const maxTotal = Math.max(...report.candidates.map((x) => x.total));
  return (
    <main className="report">
      <nav>
        <button className="icon-btn" onClick={onBack}>
          <ArrowLeft />
        </button>
        <div className="brand">
          <Seal>名</Seal>
          <span>家庭共识礼册</span>
        </div>
        <span className={`status ${report.state === "共识已形成" ? "ok" : ""}`}>
          {report.state}
        </span>
      </nav>
      {notice && <div className="toast">{notice}</div>}
      <header className="report-cover">
        <div>
          <span className="kicker">本轮共识印</span>
          <h1>{top.fullName}</h1>
          <p>{report.disagreement}</p>
        </div>
        <div className="consensus-seal">
          <b>{report.consensus}</b>
          <span>共识度</span>
        </div>
      </header>
      <PaperSection
        eyebrow="01 · 共识总览"
        title={`${top.name} 暂时领先，但理由比名次更重要`}
      >
        <div className="overview-grid">
          <div className="big-number">
            <b>{Math.round(top.total)}</b>
            <span>综合分</span>
          </div>
          <div>
            {top.strengths.map((s) => (
              <span className="reason" key={s}>
                <Check />
                {s}
              </span>
            ))}
          </div>
        </div>
      </PaperSection>
      <PaperSection
        eyebrow="02 · 候选总榜"
        title={`${report.candidates.length} 张名签，同一套标准`}
      >
        <div className="ranking">
          {report.candidates.map((r, i) => (
            <div className={i === 0 ? "rank first" : "rank"} key={r.name}>
              <span>{i + 1}</span>
              <b>{r.fullName}</b>
              <div className="rank-line">
                <i style={{ width: `${(r.total / maxTotal) * 100}%` }} />
              </div>
              <strong>{Math.round(r.total)}</strong>
            </div>
          ))}
        </div>
      </PaperSection>
      <PaperSection
        eyebrow="03 · 家庭偏好矩阵"
        title="谁在支持谁，分歧一眼看见"
      >
        <div className="matrix">
          <div />
          <>
            {report.candidates.map((r) => (
              <b key={r.name}>{r.name}</b>
            ))}
          </>
          {input.votes.map((v) => (
            <div className="matrix-row" key={v.role}>
              <strong>{v.role}</strong>
              {report.candidates.map((r) => (
                <span
                  className={
                    v.favorite === r.name
                      ? "fav"
                      : v.reservation === r.name
                        ? "hold"
                        : ""
                  }
                  key={r.name}
                >
                  {v.favorite === r.name
                    ? "首"
                    : v.reservation === r.name
                      ? "留"
                      : "·"}
                </span>
              ))}
            </div>
          ))}
        </div>
      </PaperSection>
      {locked ? (
        <Locked onUnlock={onUnlock} />
      ) : (
        <FullModules report={report} />
      )}
      <div className="report-actions">
        {locked ? (
          <button className="primary wide" onClick={onUnlock}>
            <LockKeyhole />
            解锁完整家庭礼册
          </button>
        ) : (
          <>
            <button className="primary wide" onClick={onShare}>
              <Share2 />
              分享到小红书
            </button>
            <button className="secondary wide" onClick={() => window.print()}>
              <Download />
              打印 / 存为 PDF
            </button>
          </>
        )}
      </div>
      <p className="disclaimer">
        本报告用于家庭讨论，不构成命理、户籍或法律建议。请自行核对方言谐音与户籍可用字。
      </p>
      {modal}
    </main>
  );
}

function Locked({ onUnlock }: { onUnlock: () => void }) {
  return (
    <section className="locked">
      <div className="locked-preview">
        <span className="wave">ˉ ˊ ˇ ˋ · ˊ ˉ</span>
        <div className="blur-lines">
          <i />
          <i />
          <i />
        </div>
      </div>
      <div className="lock-card">
        <LockKeyhole />
        <h2>后 11 个模块已折入礼册</h2>
        <p>
          音律波形、字形负担、意象拼图、熟悉度坐标、淘汰路径、场景压力测试、风险表、分歧诊断、前三名签、对话脚本与最终清单。
        </p>
        <button className="primary" onClick={onUnlock}>
          使用授权码展开
        </button>
      </div>
    </section>
  );
}

function FullModules({ report }: { report: ConsensusReport }) {
  const top = report.candidates[0];
  return (
    <>
      <PaperSection eyebrow="04 · 音律波形" title="读出来，看看声调怎么走">
        <div className="tone-chart">
          {report.candidates.slice(0, 3).map((r) => (
            <div key={r.name}>
              <b>{r.fullName}</b>
              <svg
                viewBox="0 0 300 80"
                role="img"
                aria-label={`${r.name}声调波形`}
              >
                <polyline
                  points={r.tones
                    .map((t, i) => `${20 + i * 130},${70 - (t || 3) * 12}`)
                    .join(" ")}
                  fill="none"
                  stroke="#a94635"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {r.tones.map((t, i) => (
                  <circle
                    key={i}
                    cx={20 + i * 130}
                    cy={70 - (t || 3) * 12}
                    r="7"
                    fill="#edf2e8"
                    stroke="#1d4c3b"
                    strokeWidth="4"
                  />
                ))}
              </svg>
              <span>{r.pinyin.join(" · ")}</span>
            </div>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="05 · 字形负担" title="不是越复杂越有分量">
        <div className="glyph-bars">
          {report.candidates.map((r) => (
            <div key={r.name}>
              <b>{r.name}</b>
              <div>
                {r.strokes.map((s, i) => (
                  <i
                    key={i}
                    style={{ height: `${Math.max(18, (s || 8) * 5)}px` }}
                  >
                    <span>{s ?? "?"}</span>
                  </i>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="06 · 文化意象拼图" title="每个名字在讲怎样的故事">
        <div className="imagery-grid">
          {report.candidates.map((r) => (
            <article key={r.name}>
              <Seal>{r.name[0]}</Seal>
              <h3>{r.name}</h3>
              <div>
                {r.imageryTags.length ? (
                  r.imageryTags.map((t) => <span key={t}>{t}</span>)
                ) : (
                  <span>待人工补充</span>
                )}
              </div>
            </article>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="07 · 熟悉度 × 辨识度" title="好认，不等于普通">
        <div className="coordinate">
          <span className="axis x">更熟悉 →</span>
          <span className="axis y">更突出 →</span>
          {report.candidates.map((r, i) => (
            <i
              key={r.name}
              style={{
                left: `${12 + r.familiarity * 0.76}%`,
                bottom: `${18 + (100 - r.familiarity) * 0.55 + (i % 2) * 8}%`,
              }}
            >
              {r.name}
            </i>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="08 · 淘汰路径" title="谁先离桌，为什么">
        <div className="path">
          {report.elimination.map((e, i) => (
            <div key={e.name}>
              <span>{report.elimination.length - i}</span>
              <b>{e.name}</b>
              <p>{e.reason}</p>
            </div>
          ))}
          <div className="winner">
            <span>印</span>
            <b>{report.leader}</b>
            <p>进入最终共识清单</p>
          </div>
        </div>
      </PaperSection>
      <PaperSection eyebrow="09 · 日常场景压力测试" title="把名字放进真实生活">
        <div className="scenario-grid">
          {["课堂点名", "电话口述", "连续书写", "家乡话朗读"].map((s, i) => (
            <article key={s}>
              <span>{["喊", "听", "写", "说"][i]}</span>
              <h3>{s}</h3>
              <p>
                {i === 0
                  ? `试着读：${top.fullName}，声调 ${top.tones.join("-")}`
                  : i === 1
                    ? "请另一位家人背对屏幕记录一次"
                    : i === 2
                      ? `名字部分约 ${top.strokes.reduce<number>((a, b) => a + (b || 0), 0)} 画（未知字除外）`
                      : "算法不判断方言，必须由家人现场复核"}
              </p>
            </article>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="10 · 风险状态表" title="先把不知道的写成不知道">
        <div className="risk-table">
          {report.candidates.map((r) => (
            <div key={r.name}>
              <b>{r.name}</b>
              <span className={r.risks.length ? "warn" : "safe"}>
                {r.risks.length
                  ? `${r.risks.length} 项待核对`
                  : "未发现显式风险"}
              </span>
              <p>
                {r.risks.join("；") || "仍建议核对方言、户籍字库与真实重名数据"}
              </p>
            </div>
          ))}
        </div>
      </PaperSection>
      <PaperSection eyebrow="11 · 分歧诊断" title="把立场换成标准">
        <div className="quote">“{report.disagreement}”</div>
        <div className="dimension-list">
          <ScoreBar label="第一名家庭支持" value={top.family} max={24} />
          <ScoreBar label="第一名日常实用" value={top.practical} max={16} />
          <ScoreBar label="第一名音律表现" value={top.sound} max={18} />
        </div>
      </PaperSection>
      <PaperSection eyebrow="12 · 前三名签" title="每个候选都留下体面的理由">
        <div className="name-cards">
          {report.candidates.slice(0, 3).map((r, i) => (
            <article key={r.name}>
              <span>第 {i + 1} 名签</span>
              <h3>{r.fullName}</h3>
              <p>{r.strengths.join("，") || "保留了较中性的综合表现"}</p>
              <small>{r.risks[0] || "进入最终轮前仍需人工朗读复核"}</small>
            </article>
          ))}
        </div>
      </PaperSection>
      <PaperSection
        eyebrow="13 · 下一轮对话脚本"
        title="少争一句，多问一个具体问题"
      >
        <ol className="conversation">
          {report.conversation.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ol>
      </PaperSection>
      <PaperSection eyebrow="14 · 共识清单" title="盖下最终一枚印之前">
        <div className="checklist">
          {[
            "全名连续朗读三次",
            "普通话和家乡话无明显负面谐音",
            "电话口述后能被准确写下",
            "家人知道自己在支持什么标准",
            "直系长辈避讳已核对",
            "户籍系统可用字已最终确认",
          ].map((x) => (
            <label key={x}>
              <input type="checkbox" />
              <span>{x}</span>
            </label>
          ))}
        </div>
      </PaperSection>
    </>
  );
}

function Unlock({
  code,
  setCode,
  busy,
  notice,
  close,
  submit,
}: {
  code: string;
  setCode: (v: string) => void;
  busy: boolean;
  notice: string;
  close: () => void;
  submit: () => void;
}) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <button className="modal-close" onClick={close}>
          <X />
        </button>
        <Seal>启</Seal>
        <h2>展开完整家庭礼册</h2>
        <p>
          授权码只在本次激活时提交给产品服务端。服务端核验成功后，完整报告生成将消耗一次权益。
        </p>
        <label className="field">
          <span>授权码</span>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="LOVE-XXXX-XXXX-XXXX"
          />
        </label>
        {notice && <div className="modal-notice">{notice}</div>}
        <button
          className="primary wide"
          disabled={busy || code.length < 12}
          onClick={submit}
        >
          {busy ? <span className="spinner" /> : <Sparkles />}
          {busy ? "正在核验并生成…" : "核验并生成完整报告"}
        </button>
        <small>生成失败后重试可能再次消耗权益；请先确认网络稳定。</small>
      </div>
    </div>
  );
}
